import React, { useState, useEffect, useRef } from "react";
import { useCallback } from "react";
import "./SalesDetailsPro.css";
import FadeUp from "../FadeUp/FadeUp";
import salesCatalogData from "../../data/salesCatalog.json";

const SalesDetailsPro = ({ isAlbanian, buildingId, categoryId}) => {
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [selectedCatalogImageUrl, setSelectedCatalogImageUrl] = useState(null);
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [projects, setProjects] = useState([]);
  const imagesLoaded = useRef(0);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startDragOffset = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const getCategoryTitle = (categoryId, isAlbanian) => {
    const id = Number(categoryId);
    if (id === 1) return isAlbanian ? "Apartamente" : "Apartments";
    if (id === 2) return isAlbanian ? "Njësi Shërbimi" : "Service Unit";
    if (id === 3) return isAlbanian ? "Njësi Parkimi" : "Car Post";
    return isAlbanian ? "Kataloget tona" : "Our Catalogs";
  };

  useEffect(() => {
    // Në vend të ApiService, filtrojmë JSON-in lokal
    const filteredData = salesCatalogData.filter(
      (item) =>
        item.building_id.toString() === buildingId.toString() &&
        item.sales_category_id.toString() === categoryId.toString()
    );
    setProjects(filteredData);
  }, [buildingId, categoryId]);

  const handleImageLoad = () => {
    imagesLoaded.current += 1;
  };

  const openPdf = (pdfUrl, catalogImageUrl, index) => {
    setSelectedPdfUrl(pdfUrl);
    setSelectedCatalogImageUrl(catalogImageUrl);
    setCurrentPdfIndex(index);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const closeOverlay = () => {
    setSelectedPdfUrl(null);
  };

  const handleNext = useCallback(() => {
    if (projects.length === 0) return;
    const nextIndex = (currentPdfIndex + 1) % projects.length;
    setCurrentPdfIndex(nextIndex);
    setSelectedPdfUrl(projects[nextIndex].pdf_url);
    setSelectedCatalogImageUrl(projects[nextIndex].catalog_image_url);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [currentPdfIndex, projects]);

  const handlePrevious = useCallback(() => {
    if (projects.length === 0) return;
    const prevIndex = (currentPdfIndex - 1 + projects.length) % projects.length;
    setCurrentPdfIndex(prevIndex);
    setSelectedPdfUrl(projects[prevIndex].pdf_url);
    setSelectedCatalogImageUrl(projects[prevIndex].catalog_image_url);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [currentPdfIndex, projects]);

  const handleDownload = () => {
    if (!selectedPdfUrl) return;
    window.open(selectedPdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = () => {
    if (!selectedPdfUrl) return;

    const linkToCopy = window.location.origin + selectedPdfUrl;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(linkToCopy)
        .then(() => alert("PDF link copied to clipboard!"))
        .catch(() => {
          const tempInput = document.createElement("input");
          tempInput.value = linkToCopy;
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand("copy");
          document.body.removeChild(tempInput);
          alert("PDF link copied!");
        });
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = linkToCopy;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      alert("PDF link copied!");
    }
  };

 
  const handleShare = (platform) => {
    if (!selectedPdfUrl) return;

    const shareUrl = encodeURIComponent(
      window.location.origin + selectedPdfUrl
    );
    let socialUrl = "";

    switch (platform) {
      case "facebook":
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
        break;
      case "twitter":
        socialUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=Check out this PDF!`;
        break;
      case "whatsapp":
        socialUrl = `https://wa.me/?text=${shareUrl}`;
        break;
      case "linkedin":
        socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
        break;
      case "telegram":
        socialUrl = `https://t.me/share/url?url=${shareUrl}&text=Check out this PDF!`;
        break;
      default:
        navigator.clipboard.writeText(window.location.origin + selectedPdfUrl);
        alert("PDF link copied to clipboard!");
        return;
    }

    window.open(socialUrl, "_blank");
  };

  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    startDragOffset.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newX =
      startDragOffset.current.initialX +
      (e.clientX - startDragOffset.current.startX);
    const newY =
      startDragOffset.current.initialY +
      (e.clientY - startDragOffset.current.startY);
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (selectedPdfUrl) {
        if (event.key === "ArrowRight") {
          handleNext();
        } else if (event.key === "ArrowLeft") {
          handlePrevious();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedPdfUrl, handleNext, handlePrevious]);

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const isLandscape =
    isSmallScreen && projects[currentPdfIndex]?.pdf_layout === "L";

  const transformStyle = isLandscape
    ? `translate(${position.x}px, ${position.y}px) scale(${zoomLevel}) rotate(90deg) scale(1.45)`
    : `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`;

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 576);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <div className="salesdetailspro-container">
      <div className="overlay">
        <div className="vertical-line left"></div>
        <div className="vertical-line right"></div>
        <div className="salesdetailspro-title-container">
          <h2 className="salesdetailspro-title">
            {getCategoryTitle(categoryId, isAlbanian)}
          </h2>
        </div>
        <div className="bottom-line"></div>
      </div>
      {projects.length === 0 ? (
        <p className="salesdetailspro-no-options-text">
          {isAlbanian
            ? "Nuk ka opsione për këtë kategori."
            : "There are no options for this category."}
        </p>
      ) : (
        <div className="salesdetailspro-grid">
          {projects.map((project, index) => (
            <FadeUp key={project.salesCatalogId}>
              <div
                className="salesdetailspro-card"
                key={project.salesCatalogId}
                onClick={() =>
                  openPdf(project.pdf_url, project.catalog_image_url, index)
                }
              >
                <div className="salesdetailspro-image-square">
                  <img
                    src={project.catalog_image_url}
                    alt={project.catalog_title}
                    className="salesdetailspro-image"
                    loading="lazy"
                    onLoad={handleImageLoad}
                  />
                </div>
                <div className="salesdetailspro-content">
                  <h3 className="salesdetailsprocard-title">
                    {isAlbanian
                      ? project.titulli_katalog
                      : project.catalog_title}
                  </h3>
                  <p className="salesdetailspro-address">
                    {isAlbanian
                      ? project.pershkrim_katalog
                      : project.catalog_description}
                  </p>
                  <p className="salesdetailspro-lookmore">
                    {isAlbanian ? "Shiko PDF→" : "Look PDF →"}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      )}

      {selectedPdfUrl && (
        <div
          className={`salesdetailspro-modal-overlay ${
            isLandscape ? "landscape" : ""
          }`}
          onClick={closeOverlay}
        >
          <button
            className="salesdetailspro-modal-close-button"
            onClick={closeOverlay}
          >
            &times;
          </button>
          <button
            className="salesdetailspro-nav-button left"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
          >
            <img
              src="/prevup.png"
              alt="Next"
              className="salesdetailspro-prevTop-btn-img"
              loading="lazy"
            />
          </button>
          <div
            className="salesdetailspro-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pdf-wrapper">
              <img
                src={selectedCatalogImageUrl}
                alt="Catalog Preview"
                className="pdf-image-viewer"
                style={{
                  transform: transformStyle,
                  transition: isDragging ? "none" : "transform 0.3s ease",
                  cursor:
                    zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "auto",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              />
            </div>

            <div className="salesdetailspro-button-container">
              <button
                onClick={handleZoomIn}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/zoomin.png"
                  alt="ZoomIn"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={handleZoomOut}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/zoomout.png"
                  alt="ZoomOut"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={handleDownload}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/download.png"
                  alt="Download"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={() => handleShare("facebook")}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/facebook.png"
                  alt="Facebook"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={() => handleShare("twitter")}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/twitter.png"
                  alt="Twitter"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={() => handleShare("whatsapp")}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/whatsapp.png"
                  alt="Whatsapp"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={() => handleShare("linkedin")}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/linke.png"
                  alt="linkedin"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={() => handleShare("telegram")}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/telegram.png"
                  alt="Telegram"
                  className="download-button-icon"
                />
              </button>

              <button
                onClick={handleCopyLink}
                className="salesdetailspro-action-button"
              >
                <img
                  src="/link.png"
                  alt="Telegram"
                  className="download-button-icon"
                />
              </button>
            </div>
          </div>

          <button
            className="salesdetailspro-nav-button right"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            <img
              src="/nextup.png"
              alt="Next"
              className="salesdetailspro-nextPro-btn-img"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesDetailsPro;
